import type { Vector3 } from '@babylonjs/core/Maths/math.vector';
import { Quaternion } from '@babylonjs/core/Maths/math.vector';
import type { Scene } from '@babylonjs/core/scene';

import { CELL_SIZE, DEFAULT_SEARCH_RADIUS_CELLS, occupiedCellKeys, snapPlacement } from '../core/GridSnap';
import type { NodeDto } from '../generated/contracts';
import type { Grabber, GrabberHub } from '../input/Grabbers';
import type { PaletteSelection } from '../input/PaletteUI';
import type { RoomHub } from '../net/roomHub';
import { toNodeKind, type NodeKindValue } from '../net/wire';
import type { RoomState } from '../room/RoomState';
import type { RoomView } from '../room/RoomView';

/**
 * Carrying a new node from the palette to where it belongs.
 *
 * The thing in your hand is the real node, not a preview: node ids are generated by the
 * client precisely so this works. It is inserted into local state immediately and confirmed
 * on release, which is why there is no pop-in and no id swap. If the server refuses it, it
 * is removed again — the only rollback in the client, and the reason optimism is confined to
 * this one case.
 *
 * Taken off the tray, and released, on the dominant trigger now — the same button a grab and a
 * connection also read (plan/mode-hierarchy.md, "Grabbing moves from the dominant grip to the
 * dominant trigger; near/far is gone" — settled 2026-08-10). `begin()` itself is only ever
 * called from a tray pick (`PaletteUI.checkForPick`/`fire`, also moved onto the trigger), so
 * `tick()` only has to read the release edge, not the press.
 */

/** Waist-to-chest height if the placement has to be guessed rather than tracked. */
const FALLBACK_HEIGHT = 1.3;

/**
 * Upright and room-aligned: the rotation a node carried out of the tray is held at,
 * regardless of the grabber's own rotation.
 *
 * Position tracks the hand every frame in `tick()`; rotation deliberately does not, because
 * there is no gesture yet that lets you turn a node while carrying it (`plan/history/controls.md`:
 * the dominant thumbstick's rotate binding is deferred). Taking the rotation from the
 * grabber instead just meant a shape came out however the controller happened to be tilted
 * when it was picked up, and kept turning with the hand for as long as it was carried — a
 * headset session reported both halves of that as one bug. `commit()` sends whatever the
 * mesh's rotation is at release, so fixing it here is what makes a placed node come out
 * upright too.
 */
const UPRIGHT_ROTATION = Quaternion.Identity();

// spec: SPEC-085
export class PlacementManager {
  private carrying: { nodeId: string; grabberId: string } | null = null;

  onPlaced: ((nodeId: string, kind: NodeKindValue) => void) | null = null;

  /** Surfaced when the grid search on placement finds no free cell nearby — see `commit()`'s
   *  own doc comment. */
  onNotice: ((message: string) => void) | null = null;

  constructor(
    private readonly scene: Scene,
    private readonly grabbers: GrabberHub,
    private readonly state: RoomState,
    private readonly view: RoomView,
    private readonly hub: RoomHub,
    private readonly context: { orgId: string; roomId: string; diagramId: string | null },
  ) {}

  get isCarrying(): boolean {
    return this.carrying !== null;
  }

  /** The node currently being carried off the tray, if any — `SnapIndicator` reads this to know
   *  which mesh's landing cell to show. */
  get carriedNodeId(): string | null {
    return this.carrying?.nodeId ?? null;
  }

  /** Begins carrying a new node, taken from the palette by this grabber. */
  begin(selection: PaletteSelection, grabber: Grabber): void {
    if (this.carrying !== null) {
      return;
    }

    const nodeId = crypto.randomUUID();

    const node: NodeDto = {
      id: nodeId,
      orgId: this.context.orgId,

      // The optimistic node's own key. Empty-string when the room is showing no diagram —
      // the server refuses the CreateNode that follows in that case, and nothing client-side
      // reads this field (every renderer keys on `id`), so a placeholder here is honest about
      // there being no diagram rather than inventing one.
      diagramId: this.context.diagramId ?? '',
      kind: selection.kind,
      shapeType: selection.shapeType ?? 'box',

      // Defaults to the catalogue's own display name ("Firewall", "Web server") — CLAUDE.md,
      // "Node.Text is a shape's label": a new shape reads as something rather than a coloured
      // blank. `selection.displayName` is undefined only for a selection that did not come
      // through the real palette catalogue (a test, mainly), in which case this falls back to
      // unlabelled exactly as before. Real text on the node from the moment it is created, not
      // a rendering-time fallback — it can be edited or cleared afterward like any other label,
      // and clearing it does not bring the default back.
      text: selection.displayName ?? null,
      style: { color: selection.color },
      transform: {
        position: { x: grabber.position.x, y: grabber.position.y, z: grabber.position.z },

        // Upright, not the grabber's rotation — see UPRIGHT_ROTATION. A shape must not spawn
        // tilted to whatever angle the controller happened to be at when it was picked up.
        rotation: {
          x: UPRIGHT_ROTATION.x,
          y: UPRIGHT_ROTATION.y,
          z: UPRIGHT_ROTATION.z,
          w: UPRIGHT_ROTATION.w,
        },
        scale: 1,
      },

      // Version 0 so the server's committed version 1 always supersedes this locally.
      version: 0,
      updatedAt: new Date().toISOString(),
    };

    this.state.upsertNode(node);
    this.carrying = { nodeId, grabberId: grabber.id };

    // Faded while in hand, like anything else being moved — and here it doubles as "this is
    // not placed yet".
    this.view.markCarried(nodeId);
  }

  start(): () => void {
    const observer = this.scene.onBeforeRenderObservable.add(() => this.tick());

    return () => this.scene.onBeforeRenderObservable.remove(observer);
  }

  private tick(): void {
    const carrying = this.carrying;

    if (carrying === null) {
      return;
    }

    const mesh = this.view.meshFor(carrying.nodeId);
    const grabber = this.grabbers.grabbers.find((candidate) => candidate.id === carrying.grabberId);

    if (!mesh) {
      this.carrying = null;
      this.view.markCarried(null);
      return;
    }

    // Lost the hand mid-carry: place it where it was last seen rather than dropping the
    // node on the floor or losing it entirely.
    if (!grabber) {
      void this.commit(carrying.nodeId, mesh.position.clone(), UPRIGHT_ROTATION);
      return;
    }

    mesh.position.copyFrom(grabber.position);

    // Rotation deliberately does not track the grabber — see UPRIGHT_ROTATION. Only
    // position follows the hand while a node is being carried out of the tray.
    (mesh.rotationQuaternion ??= Quaternion.Identity()).copyFrom(UPRIGHT_ROTATION);

    if (grabber.far.justReleased) {
      void this.commit(carrying.nodeId, mesh.position.clone(), mesh.rotationQuaternion.clone());
    }
  }

  private async commit(nodeId: string, position: Vector3, rotation: Quaternion): Promise<void> {
    const node = this.state.nodes.get(nodeId);
    this.carrying = null;
    this.view.markCarried(null);

    if (!node) {
      return;
    }

    // A node placed below the floor is unreachable, and dropping through the floor is the
    // easiest accident to have while letting go of something. Checked in room space — `y` here
    // is a physical clearance above whatever this room's own floor is, not a canonical value.
    const y = position.y < 0.1 ? FALLBACK_HEIGHT : position.y;

    // Grid snapping stays in canonical space (SPEC-131) — `state.nodes`' own positions already
    // are canonical (RoomState mirrors server truth); only the freshly-carried `position` (this
    // room's own space, off the grabber) needs converting before it joins the same arithmetic.
    // See GridSnap.ts's own module comment for what "client-side only, first iteration" leaves
    // out otherwise. Occupancy is read from whatever this client currently has in RoomState,
    // excluding the node being placed itself; there is no server-side enforcement yet, so two
    // peers placing into the same cell in the same instant can still both "win" locally. A
    // search that finds no free cell nearby does not fling this node metres away to force one —
    // it is created at its raw, unsnapped (but still canonical) position instead, and the
    // placer is told why.
    const canonicalDesired = this.view.toCanonical({ x: position.x, y, z: position.z });
    const occupied = occupiedCellKeys(
      [...this.state.nodes.values()]
        .filter((candidate) => candidate.id !== nodeId)
        .map((candidate) => candidate.transform.position),
      CELL_SIZE,
    );
    const snapped = snapPlacement(
      { x: canonicalDesired.x, y: canonicalDesired.y, z: canonicalDesired.z },
      rotation,
      occupied,
      CELL_SIZE,
      DEFAULT_SEARCH_RADIUS_CELLS,
    );

    if (!snapped.snappedToCell) {
      this.onNotice?.('no room on the grid nearby — placed without snapping');
    }

    try {
      const created = await this.hub.createNode({
        nodeId,
        kind: toNodeKind(node.kind),
        shapeType: node.shapeType,
        text: node.text,
        style: node.style,
        transform: {
          position: snapped.position,
          rotation: {
            x: snapped.rotation.x,
            y: snapped.rotation.y,
            z: snapped.rotation.z,
            w: snapped.rotation.w,
          },
          scale: 1,
        },
      });

      // The confirmed node replaces the optimistic one. Not cosmetic: the local copy still
      // carries the pose the node had when it was taken off the tray, and the next refresh of
      // it — a style edit, a reconnect — would apply that and snap the node back to the tray.
      this.state.upsertNode(created);

      this.onPlaced?.(nodeId, toNodeKind(node.kind));
    } catch (error) {
      // The server refused it, so it never existed. Take it back out — and say why, the same
      // "an honest, server-worded notice" every other refusal in this project surfaces
      // through `onNotice` (CLAUDE.md, "A callback's own argument is the value that was true
      // when it fired"). This used to only `console.warn`, which meant the 100-node room cap
      // (plan/open-items.md) — the first refusal `CreateNode` could ever produce that is not
      // this client's own fault — would have failed silently: the carried node simply
      // vanished from your hand with no explanation.
      console.warn('Could not place node; removing the local copy.', error);
      this.state.removeNode(nodeId);
      this.onNotice?.(error instanceof Error ? error.message : String(error));
    }
  }

  /** Abandons the carry, removing the optimistic node. */
  cancel(): void {
    if (this.carrying === null) {
      return;
    }

    this.state.removeNode(this.carrying.nodeId);
    this.carrying = null;
    this.view.markCarried(null);
  }
}
